import React, { useCallback } from 'react';

interface CurrencyInputProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Formata valor para moeda brasileira (R$ 1.234,56)
 * Armazena internamente como string numerica (1234.56)
 */
export const formatToBRL = (value: string | number): string => {
  const num = typeof value === 'string' ? value.replace(/\D/g, '') : String(Math.round(value * 100));
  if (!num || num === '0') return '';

  const cents = num.padStart(3, '0');
  const reais = cents.slice(0, -2);
  const centavos = cents.slice(-2);

  const reaisFormatted = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `R$ ${reaisFormatted},${centavos}`;
};

export const parseBRLToNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10) / 100;
  return num.toFixed(2);
};

export const numberToBRLDisplay = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return formatToBRL(num);
};

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  name, value, onChange, label, required, disabled, placeholder, className
}) => {
  const displayValue = value ? numberToBRLDisplay(value) : '';

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const numericValue = parseBRLToNumber(rawInput);

    // Criar evento sintetico com o valor numerico para o form pai
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name,
        value: numericValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  }, [name, onChange]);

  return (
    <div>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        type="text"
        inputMode="numeric"
        id={name}
        name={name}
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder || 'R$ 0,00'}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}
      />
    </div>
  );
};

export default CurrencyInput;
